// Inventory arithmetic: what to reorder, what to buy for which job, what is on order, when a
// material runs short, what it has been costing. Pure, like the rest of the engine: rows in,
// answers out, exact arithmetic, and the caller passes in today's date.
//
// Quantities come back as decimal text (exact), money as integer cents.

import { allocateCents, Rat } from './rational.ts';

export type Num = string | number | null | undefined;

function rat(v: Num): Rat {
  if (v === null || v === undefined || v === '') return Rat.ZERO;
  return Rat.tryParse(v) ?? Rat.ZERO;
}
function has(v: Num): boolean {
  return v !== null && v !== undefined && v !== '' && Rat.tryParse(v) !== null;
}
const max0 = (r: Rat) => (r.sign() < 0 ? Rat.ZERO : r);
const text = (r: Rat) => r.toDecimal(6);

/** Add quantities exactly. Blank counts as nothing. */
export function total(values: Num[]): string {
  return text(values.reduce<Rat>((a, v) => a.add(rat(v)), Rat.ZERO));
}

export interface InvMaterial {
  id: string;
  /** `exact` counts units; `bin` is for consumables nobody counts, so its on-hand figure means nothing. */
  tracking_mode: 'exact' | 'bin' | string;
  stock_on_hand: Num;
  safety_stock: Num;
  order_quantity: Num;
}

// ---------- tiers: how sure is the quantity ----------

/**
 * firm      the job has a locked revision (Rev 0 or later): buy it.
 * committed the opportunity is won but nothing is locked yet: pullable, line by line.
 * likely    still open before Won: never a buy line, only a forecast.
 * none      lost, or not a lead.
 *
 * Starting a revision on a locked job does not make it committed again: the last locked
 * revision still stands until the next one replaces it.
 */
export type Tier = 'firm' | 'committed' | 'likely' | 'none';

export function jobTier(job: { has_revision: boolean; outcome: string | null | undefined }): Tier {
  if (job.outcome === 'lost' || job.outcome === 'not_a_lead') return 'none';
  if (job.has_revision) return 'firm';
  if (job.outcome === 'won') return 'committed';
  return 'likely';
}

/** Total quantity per material in a resolved job's BOM (live, or a stored revision's). */
export function needByMaterial(groups: { bom: { material_id: string | null; qty: string }[] }[]): Record<string, string> {
  const sum = new Map<string, Rat>();
  for (const g of groups) {
    for (const b of g.bom) {
      if (!b.material_id) continue;
      const q = rat(b.qty);
      if (q.sign() > 0) sum.set(b.material_id, (sum.get(b.material_id) ?? Rat.ZERO).add(q));
    }
  }
  return Object.fromEntries([...sum].map(([id, q]) => [id, text(q)]));
}

/** A vendor page typed on a job item for a material, with the item that typed it (an allowance line's link). */
export interface MaterialLink {
  number: string;
  url: string;
}

/**
 * Where each material is bought, from the items' own typed links (an allowance line: the butcher block page on the
 * vendor's site), so the buyer opens the page from the buy list. A page typed on two items is listed once per item.
 */
export function linksByMaterial(groups: { bom: { material_id: string | null; links?: { number: string; url: string }[] }[] }[]): Record<string, MaterialLink[]> {
  const out: Record<string, MaterialLink[]> = {};
  for (const g of groups) {
    for (const b of g.bom) {
      if (!b.material_id || !b.links?.length) continue;
      const list = (out[b.material_id] ??= []);
      for (const l of b.links) if (l.url && !list.some((x) => x.number === l.number && x.url === l.url)) list.push({ number: l.number, url: l.url });
    }
  }
  return out;
}

/**
 * What a job's estimate says one unit of a material costs, for a material that carries no price of its own (an
 * allowance priced on the item): the estimated dollars over the estimated quantity, in cents. Null with nothing estimated.
 */
export function estimatedUnitCents(estimate: { qty: string; cents: number } | undefined): number | null {
  if (!estimate) return null;
  const q = rat(estimate.qty);
  if (q.sign() <= 0) return null;
  return Number(Rat.of(BigInt(estimate.cents)).div(q).toDecimal(0));
}

// ---------- buy lines ----------

export interface DemandJob {
  id: string;
  tier: Tier;
  /** Lower goes first when free stock is shared out (earlier build window first). */
  order: number;
  /** material id → quantity the job needs: from its locked revision when firm, live when committed. */
  need: Record<string, Num>;
}

/** A quantity already put on purchasing for a job: pulled early, or put on an order. */
export interface DemandRow {
  job_id: string;
  material_id: string;
  qty: Num;
  /** How much of it has arrived. */
  received: Num;
  early: boolean;
}

export interface BuyLine {
  job_id: string;
  material_id: string;
  tier: Tier;
  need: string;
  /** Already used on this job (cards closed). */
  consumed: string;
  /** Already on purchasing for this job: pulled, ordered or received. */
  covered: string;
  /** Taken from stock that no other job has a claim on. */
  from_stock: string;
  /** What is still to buy. */
  buy: string;
  pulled: boolean;
}

/**
 * What each firm and committed job still has to buy, material by material.
 *
 *   still needed = need − already consumed by the job
 *   covered      = what the job already has on purchasing (arrived or not)
 *   gap          = still needed − covered
 *   from stock   = free stock shared to the gaps, firm jobs first, then by order
 *   buy          = gap − from stock
 *
 * Free stock is stock on hand that no job has a claim on: units received against a job's
 * own line and not yet used by it are that job's, and are not shared to another.
 * Bin-counted materials are consumables nobody counts; they are never bought per job.
 */
export function buyLines(
  jobs: DemandJob[],
  materials: InvMaterial[],
  rows: DemandRow[],
  /** job id → material id → quantity consumed */
  consumed: Record<string, Record<string, Num>>,
): BuyLine[] {
  const mat = new Map(materials.map((m) => [m.id, m]));
  const key = (job: string, material: string) => `${job}|${material}`;
  const rowQty = new Map<string, Rat>();
  const rowReceived = new Map<string, Rat>();
  const pulled = new Set<string>();
  for (const r of rows) {
    const k = key(r.job_id, r.material_id);
    rowQty.set(k, (rowQty.get(k) ?? Rat.ZERO).add(max0(rat(r.qty))));
    rowReceived.set(k, (rowReceived.get(k) ?? Rat.ZERO).add(max0(rat(r.received))));
    if (r.early) pulled.add(k);
  }
  const used = (job: string, material: string) => max0(rat(consumed[job]?.[material]));

  // stock a job has received for itself and not used yet is spoken for
  const free = new Map<string, Rat>();
  for (const m of materials) free.set(m.id, max0(rat(m.stock_on_hand)));
  for (const [k, received] of rowReceived) {
    const [job, material] = k.split('|') as [string, string];
    const held = max0(received.sub(used(job, material)));
    if (held.sign() > 0 && free.has(material)) free.set(material, max0(free.get(material)!.sub(held)));
  }

  const rank = (t: Tier) => (t === 'firm' ? 0 : 1);
  const ordered = jobs.filter((j) => j.tier === 'firm' || j.tier === 'committed').sort((a, b) => rank(a.tier) - rank(b.tier) || a.order - b.order || a.id.localeCompare(b.id));

  const out: BuyLine[] = [];
  for (const j of ordered) {
    const ids = new Set([...Object.keys(j.need), ...rows.filter((r) => r.job_id === j.id).map((r) => r.material_id)]);
    for (const id of ids) {
      const m = mat.get(id);
      if (!m || m.tracking_mode === 'bin') continue;
      const need = max0(rat(j.need[id]));
      const k = key(j.id, id);
      const consumedHere = used(j.id, id);
      const covered = rowQty.get(k) ?? Rat.ZERO;
      // what is covered but already used up no longer counts toward what is still needed
      const stillNeeded = max0(need.sub(consumedHere));
      const coveredLeft = max0(covered.sub(consumedHere));
      const gap = max0(stillNeeded.sub(coveredLeft));
      const available = free.get(id) ?? Rat.ZERO;
      const fromStock = gap.cmp(available) <= 0 ? gap : available;
      free.set(id, available.sub(fromStock));
      if (need.isZero() && covered.isZero()) continue;
      out.push({
        job_id: j.id,
        material_id: id,
        tier: j.tier,
        need: text(need),
        consumed: text(consumedHere),
        covered: text(covered),
        from_stock: text(fromStock),
        buy: text(gap.sub(fromStock)),
        pulled: pulled.has(k),
      });
    }
  }
  return out;
}

// ---------- purchase orders ----------

/** The five states an order moves through. The keys are fixed; their names are a setting. */
export type OrderState = 'draft' | 'placed' | 'partly_received' | 'received' | 'closed';
export const ORDER_STATES: OrderState[] = ['draft', 'placed', 'partly_received', 'received', 'closed'];

export interface OrderLineIn {
  qty: Num;
  closed_short_qty: Num;
  /** The sum of the receipts against this line. */
  received: Num;
}

/** Ordered, not cancelled, not yet arrived. */
export function lineOutstanding(l: OrderLineIn): Rat {
  return max0(rat(l.qty).sub(max0(rat(l.closed_short_qty))).sub(max0(rat(l.received))));
}

/**
 * An order's state is worked out, never typed:
 *
 *   closed           someone closed it (the paperwork is done)
 *   draft            not placed yet
 *   received         placed, and nothing more is coming (every line arrived or was closed short)
 *   partly received  placed, something has arrived, something is still coming (backordered)
 *   placed           placed, nothing has arrived
 */
export function orderState(order: { placed_at: string | null | undefined; closed_at: string | null | undefined }, lines: OrderLineIn[]): OrderState {
  if (order.closed_at) return 'closed';
  if (!order.placed_at) return 'draft';
  const outstanding = lines.reduce((a, l) => a.add(lineOutstanding(l)), Rat.ZERO);
  const received = lines.reduce((a, l) => a.add(max0(rat(l.received))), Rat.ZERO);
  if (lines.length > 0 && outstanding.isZero()) return 'received';
  if (received.sign() > 0) return 'partly_received';
  return 'placed';
}

/** What is on order per material: the outstanding quantity on orders that are placed and not closed. A draft is not on order. */
export function onOrder(orders: { placed_at: string | null | undefined; closed_at: string | null | undefined; lines: (OrderLineIn & { material_id: string })[] }[]): Record<string, string> {
  const sum = new Map<string, Rat>();
  for (const o of orders) {
    if (!o.placed_at || o.closed_at) continue;
    for (const l of o.lines) {
      const out = lineOutstanding(l);
      if (out.sign() > 0) sum.set(l.material_id, (sum.get(l.material_id) ?? Rat.ZERO).add(out));
    }
  }
  return Object.fromEntries([...sum].map(([id, q]) => [id, text(q)]));
}

/**
 * Share an order line among the requests it covers, in the order given.
 *
 * What the line can still deliver is its quantity less what was closed short; each request
 * is covered up to its own quantity until that runs out, so closing a line short (or
 * trimming it on a draft) uncovers the last requests and their need shows again. What has
 * arrived is handed out the same way. Anything over the requests is for stock.
 */
export function shareOrderLine(line: OrderLineIn, requests: { id: string; qty: Num }[]): Record<string, { qty: string; received: string }> {
  let supply = max0(rat(line.qty).sub(max0(rat(line.closed_short_qty))));
  let arrived = max0(rat(line.received));
  const out: Record<string, { qty: string; received: string }> = {};
  for (const r of requests) {
    const want = max0(rat(r.qty));
    const covered = want.cmp(supply) <= 0 ? want : supply;
    const got = covered.cmp(arrived) <= 0 ? covered : arrived;
    supply = supply.sub(covered);
    arrived = arrived.sub(got);
    out[r.id] = { qty: text(covered), received: text(got) };
  }
  return out;
}

// ---------- lead time ----------

/**
 * Days from ordering to arrival. The material's own figure applies to its primary vendor;
 * a buy from any other vendor uses that vendor's default. Unknown is null, never zero:
 * a lead time nobody has entered is not "arrives today".
 */
export function leadTime(
  material: { vendor_id: string | null | undefined; lead_time_days: number | string | null | undefined },
  vendor: { id: string; lead_time_days: number | string | null | undefined } | null | undefined,
): number | null {
  const own = material.lead_time_days;
  if (own !== null && own !== undefined && own !== '' && (!vendor || vendor.id === material.vendor_id)) return Number(own);
  const dflt = vendor?.lead_time_days;
  return dflt === null || dflt === undefined || dflt === '' ? null : Number(dflt);
}

/** A calendar day `days` after `from` (both as yyyy-mm-dd). */
export function addDays(from: string, days: number): string {
  const [y, m, d] = from.slice(0, 10).split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** The same day of the month `months` earlier, pulled back to the month's last day when it has no such day. */
export function monthsBefore(from: string, months: number): string {
  const [y, m, d] = from.slice(0, 10).split('-').map(Number) as [number, number, number];
  const first = new Date(Date.UTC(y, m - 1 - months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d, last))).toISOString().slice(0, 10);
}

// ---------- prices ----------

export interface Purchase {
  /** yyyy-mm-dd, or a timestamp starting with it. */
  bought_at: string;
  qty: Num;
  unit_price: Num;
  vendor_id?: string | null;
}

export interface PriceSummary {
  /** Unit price of the most recent purchase, to 4 places. Null when nothing has been bought. */
  last_paid: string | null;
  last_paid_at: string | null;
  last_vendor_id: string | null;
  /** Quantity-weighted average over the window, to 4 places. Null when nothing was bought in it. */
  average: string | null;
  /** How many purchases the average is made of. */
  average_count: number;
  window_months: number;
  /** Last paid is not the estimating price (compared to 4 places), or there is no estimating price to compare. */
  differs: boolean;
}

/**
 * What a material has been costing, beside the price estimates use.
 * The estimating price never changes on its own; `differs` is the flag that gets it looked at.
 */
export function priceSummary(purchases: Purchase[], estimatingPrice: Num, today: string, windowMonths: number): PriceSummary {
  const valid = purchases.filter((p) => has(p.unit_price) && rat(p.qty).sign() > 0);
  let last: Purchase | null = null;
  for (const p of valid) if (!last || p.bought_at.slice(0, 10) >= last.bought_at.slice(0, 10)) last = p;
  const from = monthsBefore(today, windowMonths);
  const inWindow = valid.filter((p) => p.bought_at.slice(0, 10) >= from && p.bought_at.slice(0, 10) <= today.slice(0, 10));
  const units = inWindow.reduce((a, p) => a.add(rat(p.qty)), Rat.ZERO);
  const spent = inWindow.reduce((a, p) => a.add(rat(p.qty).mul(rat(p.unit_price))), Rat.ZERO);
  const lastPaid = last ? rat(last.unit_price).toFixed(4) : null;
  return {
    last_paid: lastPaid,
    last_paid_at: last ? last.bought_at.slice(0, 10) : null,
    last_vendor_id: last?.vendor_id ?? null,
    average: units.sign() > 0 ? spent.div(units).toFixed(4) : null,
    average_count: inWindow.length,
    window_months: windowMonths,
    differs: lastPaid !== null && (!has(estimatingPrice) || rat(estimatingPrice).toFixed(4) !== lastPaid),
  };
}

export interface ShippingRule {
  shipping_rule: 'flat' | 'free_over' | 'percent' | string | null | undefined;
  shipping_amount: Num;
  shipping_threshold: Num;
  shipping_percent: Num;
}

/**
 * A vendor's usual shipping on an order of this value, for before the real figure exists.
 * Null when the vendor has no rule: unknown, not free.
 */
export function shippingEstimate(vendor: ShippingRule | null | undefined, orderCents: number): number | null {
  if (!vendor?.shipping_rule) return null;
  if (vendor.shipping_rule === 'flat') return has(vendor.shipping_amount) ? rat(vendor.shipping_amount).toCents() : null;
  if (vendor.shipping_rule === 'free_over') {
    if (!has(vendor.shipping_threshold) || !has(vendor.shipping_amount)) return null;
    // an order that reaches the threshold ships free
    return orderCents >= rat(vendor.shipping_threshold).toCents() ? 0 : rat(vendor.shipping_amount).toCents();
  }
  if (vendor.shipping_rule === 'percent') return has(vendor.shipping_percent) ? Number(Rat.of(BigInt(orderCents)).mul(rat(vendor.shipping_percent)).div(Rat.of(100n)).round()) : null;
  return null;
}

/** An order line's value in cents: quantity actually bought times the unit price. */
export function lineCents(qty: Num, unitPrice: Num): number {
  return rat(qty).mul(rat(unitPrice)).toCents();
}

/**
 * Spread an order's shipping and tax over its lines by value, so each line (and through it
 * each job) carries its share and the shares add up to the cent.
 */
export function allocateActuals(lines: { id: string; cents: number }[], shippingCents: number, taxCents: number): Record<string, { shipping: number; tax: number }> {
  const shares = lines.map((l) => Rat.of(BigInt(Math.max(0, l.cents))));
  const ship = allocateCents(shippingCents, shares);
  const tax = allocateCents(taxCents, shares);
  return Object.fromEntries(lines.map((l, i) => [l.id, { shipping: ship[i] ?? 0, tax: tax[i] ?? 0 }]));
}

// ---------- supply and demand ----------

export interface SdDemandIn {
  material_id: string;
  job_id: string;
  tier: Tier;
  /** What the job still has to draw: its need less what it has already used. */
  qty: Num;
  /** The day the job's build window starts (yyyy-mm-dd), or null when it has none. */
  needed_on: string | null;
  /** 0–100. Only read for likely jobs; blank counts as 0. */
  likelihood_pct?: Num;
}

export interface SdArrivalIn {
  material_id: string;
  qty: Num;
  /** yyyy-mm-dd, or null when the lead time is not known. */
  expected_on: string | null;
}

export interface SdMonth {
  /** yyyy-mm */
  month: string;
  firm: string;
  committed: string;
  /** Likely demand, already weighted by each job's likelihood. */
  likely: string;
  arriving: string;
  /** On hand + arrivals − firm − committed, running to the end of this month. Null when the material is not counted. */
  balance: string | null;
  /** The same with likely demand taken off as well. */
  balance_with_likely: string | null;
}

export interface SdRow {
  material_id: string;
  /** False for a bin-counted material: it has no on-hand figure, so no balance. */
  counted: boolean;
  on_hand: string | null;
  on_order: string;
  /** On order with no expected date: real, but not placed in any month. */
  on_order_undated: string;
  firm: string;
  committed: string;
  likely: string;
  /** Demand from jobs with no build window: it counts in the totals but in no month. */
  undated: { firm: string; committed: string; likely: string };
  months: SdMonth[];
  /** The first month the balance goes below zero on firm and committed demand. */
  first_short: string | null;
  /** The same once likely demand is counted. */
  first_short_with_likely: string | null;
  /** Firm + committed demand beyond everything on hand and on order: what an order would have to cover. */
  shortfall: string;
  /** Consumption per month over the trailing window, oldest first, this month last. */
  usage: { month: string; qty: string }[];
  usage_per_month: string;
}

const monthOf = (day: string) => day.slice(0, 7);
function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
}

/**
 * Per material: what there is, what is coming, and what the jobs will draw, month by month
 * out to the last build window.
 *
 * Demand keeps its three tiers apart: firm (locked jobs), committed (won, not locked) and
 * likely (not yet won, multiplied by the opportunity's likelihood). A job's demand falls in
 * the month its build window starts; a window already under way, and an arrival already
 * due, fall in this month. A job with no window is counted in the totals but in no month.
 */
export function supplyDemand(
  materials: InvMaterial[],
  demand: SdDemandIn[],
  arrivals: SdArrivalIn[],
  /** consumption by month: [{ material_id, month: 'yyyy-mm', qty }] */
  usage: { material_id: string; month: string; qty: Num }[],
  today: string,
  usageMonths: number,
): SdRow[] {
  const thisMonth = monthOf(today);
  const clamp = (day: string) => (monthOf(day) < thisMonth ? thisMonth : monthOf(day));
  const weighted = (d: SdDemandIn): Rat => {
    const q = max0(rat(d.qty));
    if (d.tier !== 'likely') return q;
    const pct = rat(d.likelihood_pct);
    const p = pct.sign() < 0 ? Rat.ZERO : pct.cmp(Rat.of(100n)) > 0 ? Rat.of(100n) : pct;
    return q.mul(p).div(Rat.of(100n));
  };

  const out: SdRow[] = [];
  for (const m of materials) {
    const ds = demand.filter((d) => d.material_id === m.id && (d.tier === 'firm' || d.tier === 'committed' || d.tier === 'likely'));
    const as = arrivals.filter((a) => a.material_id === m.id && rat(a.qty).sign() > 0);
    const us = usage.filter((u) => u.material_id === m.id);
    const counted = m.tracking_mode !== 'bin';

    const last = [...ds.filter((d) => d.needed_on).map((d) => clamp(d.needed_on!)), ...as.filter((a) => a.expected_on).map((a) => clamp(a.expected_on!)), thisMonth].sort().pop()!;
    const months: SdMonth[] = [];
    const zero = () => ({ firm: Rat.ZERO, committed: Rat.ZERO, likely: Rat.ZERO });
    const totals = zero();
    const undated = zero();
    let balance = rat(m.stock_on_hand);
    let withLikely = balance;
    let firstShort: string | null = null;
    let firstShortLikely: string | null = null;
    for (let month = thisMonth; month <= last; month = addMonths(month, 1)) {
      const here = zero();
      for (const d of ds) if (d.needed_on && clamp(d.needed_on) === month) here[d.tier as 'firm' | 'committed' | 'likely'] = here[d.tier as 'firm' | 'committed' | 'likely'].add(weighted(d));
      const arriving = as.filter((a) => a.expected_on && clamp(a.expected_on) === month).reduce((s, a) => s.add(rat(a.qty)), Rat.ZERO);
      balance = balance.add(arriving).sub(here.firm).sub(here.committed);
      withLikely = withLikely.add(arriving).sub(here.firm).sub(here.committed).sub(here.likely);
      if (counted && firstShort === null && balance.sign() < 0) firstShort = month;
      if (counted && firstShortLikely === null && withLikely.sign() < 0) firstShortLikely = month;
      months.push({ month, firm: text(here.firm), committed: text(here.committed), likely: text(here.likely), arriving: text(arriving), balance: counted ? text(balance) : null, balance_with_likely: counted ? text(withLikely) : null });
    }
    for (const d of ds) {
      const tier = d.tier as 'firm' | 'committed' | 'likely';
      totals[tier] = totals[tier].add(weighted(d));
      if (!d.needed_on) undated[tier] = undated[tier].add(weighted(d));
    }
    const onOrderAll = as.reduce((s, a) => s.add(rat(a.qty)), Rat.ZERO);
    const onOrderUndated = as.filter((a) => !a.expected_on).reduce((s, a) => s.add(rat(a.qty)), Rat.ZERO);

    const window: { month: string; qty: string }[] = [];
    let used = Rat.ZERO;
    for (let i = Math.max(1, usageMonths) - 1; i >= 0; i--) {
      const month = addMonths(thisMonth, -i);
      const q = us.filter((u) => u.month === month).reduce((s, u) => s.add(max0(rat(u.qty))), Rat.ZERO);
      used = used.add(q);
      window.push({ month, qty: text(q) });
    }

    out.push({
      material_id: m.id,
      counted,
      on_hand: counted ? text(rat(m.stock_on_hand)) : null,
      on_order: text(onOrderAll),
      on_order_undated: text(onOrderUndated),
      firm: text(totals.firm),
      committed: text(totals.committed),
      likely: text(totals.likely),
      undated: { firm: text(undated.firm), committed: text(undated.committed), likely: text(undated.likely) },
      months,
      first_short: firstShort,
      first_short_with_likely: firstShortLikely,
      shortfall: counted ? text(max0(totals.firm.add(totals.committed).sub(rat(m.stock_on_hand)).sub(onOrderAll))) : '0',
      usage: window,
      usage_per_month: text(used.div(Rat.of(BigInt(Math.max(1, usageMonths))))),
    });
  }
  return out;
}

// ---------- connections: the job, the shop board, the schedule ----------

/** What a job is still waiting on for one material: what has not been ordered yet, plus what is ordered and has not arrived. */
export function stillToCome(buy: Num, requests: { qty: Num; received: Num }[]): string {
  return text(requests.reduce((a, r) => a.add(max0(rat(r.qty).sub(max0(rat(r.received))))), max0(rat(buy))));
}

/** What a job took, or will take, from the shelf rather than from its own purchases: what it has used beyond what arrived for it, plus what stock is set aside for the rest. */
export function stockDraw(consumed: Num, receivedForJob: Num, fromStock: Num): string {
  return text(max0(rat(consumed).sub(max0(rat(receivedForJob)))).add(max0(rat(fromStock))));
}

/**
 * A job's estimated materials: quantity and cost per material, from its bill of materials (the locked revision's,
 * once it has one). `typed` is true when a line for the material took a price typed on the job (an allowance), so
 * what the job expects to pay is the estimate's price, not the material's own.
 */
export function estimateByMaterial(groups: { bom: { material_id: string | null; qty: string; cents: number; typed_price?: boolean }[] }[]): Record<string, { qty: string; cents: number; typed: boolean }> {
  const out = new Map<string, { qty: Rat; cents: number; typed: boolean }>();
  for (const g of groups) {
    for (const b of g.bom) {
      if (!b.material_id) continue;
      const q = rat(b.qty);
      if (q.sign() <= 0) continue;
      const at = out.get(b.material_id) ?? { qty: Rat.ZERO, cents: 0, typed: false };
      out.set(b.material_id, { qty: at.qty.add(q), cents: at.cents + b.cents, typed: at.typed || !!b.typed_price });
    }
  }
  return Object.fromEntries([...out].map(([id, v]) => [id, { qty: text(v.qty), cents: v.cents, typed: v.typed }]));
}

export interface OrderCostLine {
  id: string;
  qty: Num;
  closed_short_qty: Num;
  est_unit_price: Num;
  actual_unit_price: Num;
  /** The requests this line covers, each with the quantity it is covered for (see shareOrderLine). */
  requests: { id: string; qty: Num }[];
}

export interface RequestCost {
  qty: string;
  /** Quantity × the unit price paid; the estimated price stands in until the invoice is in. */
  cents: number;
  /** False while the line has no actual price. */
  priced: boolean;
  /** This request's share of the order's shipping and tax. */
  shipping: number;
  tax: number;
}

/**
 * What each request on an order cost. Shipping and tax are spread over the lines by value
 * and, within a line, over its requests by quantity; what a line bought beyond its requests
 * (for the shelf) carries its own share, so a job is never charged for stock.
 */
export function orderRequestCosts(lines: OrderCostLine[], shippingCents: number, taxCents: number): Record<string, RequestCost> {
  const bought = (l: OrderCostLine) => max0(rat(l.qty).sub(max0(rat(l.closed_short_qty))));
  const price = (l: OrderCostLine) => (has(l.actual_unit_price) ? rat(l.actual_unit_price) : rat(l.est_unit_price));
  const byLine = allocateActuals(lines.map((l) => ({ id: l.id, cents: bought(l).mul(price(l)).toCents() })), shippingCents, taxCents);
  const out: Record<string, RequestCost> = {};
  for (const l of lines) {
    const asked = l.requests.map((r) => max0(rat(r.qty)));
    const spare = max0(bought(l).sub(asked.reduce((a, q) => a.add(q), Rat.ZERO)));
    const shares = [...asked, spare];
    const ship = allocateCents(byLine[l.id]?.shipping ?? 0, shares);
    const tax = allocateCents(byLine[l.id]?.tax ?? 0, shares);
    l.requests.forEach((r, i) => {
      out[r.id] = { qty: text(asked[i]!), cents: asked[i]!.mul(price(l)).toCents(), priced: has(l.actual_unit_price), shipping: ship[i] ?? 0, tax: tax[i] ?? 0 };
    });
  }
  return out;
}

export interface MaterialVarianceRow {
  material_id: string;
  est_qty: string;
  est_cents: number;
  bought_qty: string;
  bought_cents: number;
  /** Taken from the shelf rather than bought for the job, valued at the estimating price. */
  stock_qty: string;
  stock_cents: number;
  /** Bought + from stock, less the estimate. Above zero is over. */
  variance_cents: number;
  /** Some of what was bought has no actual price yet, so the estimated price is standing in. */
  unpriced: boolean;
  /** Still to buy or still to arrive. */
  open_qty: string;
}

export interface JobMaterialVariance {
  rows: MaterialVarianceRow[];
  est_cents: number;
  bought_cents: number;
  stock_cents: number;
  shipping_cents: number;
  tax_cents: number;
  /** Materials + shipping + tax, less the estimate. */
  variance_cents: number;
  /** Nothing left to buy or to arrive, and every purchase has its actual price. */
  complete: boolean;
}

/**
 * Estimated against bought, material by material, for one job.
 * Shipping and tax are their own rows: the estimate has no line for them.
 */
export function jobMaterialVariance(
  estimate: Record<string, { qty: Num; cents: number }>,
  /** this job's requests on orders, with their costs (see orderRequestCosts) */
  bought: { material_id: string; cost: RequestCost }[],
  /** per material: drawn from the shelf for this job, and what is still open */
  lines: { material_id: string; stock_qty: Num; unit_price: Num; open_qty: Num }[],
): JobMaterialVariance {
  const ids = [...new Set([...Object.keys(estimate), ...bought.map((b) => b.material_id), ...lines.map((l) => l.material_id)])];
  let shipping = 0;
  let tax = 0;
  const rows: MaterialVarianceRow[] = ids.map((id) => {
    const mine = bought.filter((b) => b.material_id === id);
    const line = lines.find((l) => l.material_id === id);
    for (const b of mine) {
      shipping += b.cost.shipping;
      tax += b.cost.tax;
    }
    const boughtCents = mine.reduce((n, b) => n + b.cost.cents, 0);
    const stockQty = max0(rat(line?.stock_qty));
    const stockCents = stockQty.mul(rat(line?.unit_price)).toCents();
    const est = estimate[id];
    return {
      material_id: id,
      est_qty: text(rat(est?.qty)),
      est_cents: est?.cents ?? 0,
      bought_qty: total(mine.map((b) => b.cost.qty)),
      bought_cents: boughtCents,
      stock_qty: text(stockQty),
      stock_cents: stockCents,
      variance_cents: boughtCents + stockCents - (est?.cents ?? 0),
      unpriced: mine.some((b) => !b.cost.priced),
      open_qty: text(max0(rat(line?.open_qty))),
    };
  });
  const sum = (f: (r: MaterialVarianceRow) => number) => rows.reduce((n, r) => n + f(r), 0);
  const est = sum((r) => r.est_cents);
  const boughtCents = sum((r) => r.bought_cents);
  const stockCents = sum((r) => r.stock_cents);
  return {
    rows,
    est_cents: est,
    bought_cents: boughtCents,
    stock_cents: stockCents,
    shipping_cents: shipping,
    tax_cents: tax,
    variance_cents: boughtCents + stockCents + shipping + tax - est,
    complete: rows.length > 0 && rows.every((r) => !r.unpriced && Number(r.open_qty) === 0),
  };
}

export interface LeadItemIn {
  material_id: string;
  /** Already on a placed order. */
  ordered: boolean;
  /** Ordered: the day it is expected, or null when that is not known. */
  expected_on?: string | null;
  /** Not ordered: days from ordering to arrival, or null when the lead time is not known. */
  lead_days?: number | null;
}

export interface MaterialReadiness {
  /** The day everything outstanding could be in hand, if what is not ordered yet were ordered today. Null when nothing is outstanding or nothing can be dated. */
  earliest_start: string | null;
  /** The item that sets that day. */
  longest: { material_id: string; arrives_on: string; lead_days: number | null } | null;
  /** The longest lead among items not yet ordered: how far ahead of the build the order (and so the lock) has to happen. */
  longest_unordered: { material_id: string; lead_days: number } | null;
  /** Outstanding items whose arrival cannot be worked out: no lead time entered, or an order with no expected date. */
  unknown: string[];
}

/**
 * When a job's materials can all be in hand. Pass only what is still outstanding for the
 * job; anything already on the shelf or received is not a constraint. An item with no lead
 * time is reported as unknown, never treated as arriving today.
 */
export function materialReadiness(items: LeadItemIn[], today: string): MaterialReadiness {
  let longest: MaterialReadiness['longest'] = null;
  let unordered: MaterialReadiness['longest_unordered'] = null;
  const unknown: string[] = [];
  for (const it of items) {
    let arrives: string | null = null;
    let days: number | null = null;
    if (it.ordered) {
      // something overdue is expected any day: it cannot arrive in the past
      arrives = it.expected_on ? (it.expected_on.slice(0, 10) < today.slice(0, 10) ? today.slice(0, 10) : it.expected_on.slice(0, 10)) : null;
    } else if (it.lead_days !== null && it.lead_days !== undefined) {
      days = Number(it.lead_days);
      arrives = addDays(today, days);
      if (!unordered || days > unordered.lead_days) unordered = { material_id: it.material_id, lead_days: days };
    }
    if (arrives === null) {
      unknown.push(it.material_id);
      continue;
    }
    if (!longest || arrives > longest.arrives_on) longest = { material_id: it.material_id, arrives_on: arrives, lead_days: days };
  }
  return { earliest_start: longest?.arrives_on ?? null, longest, longest_unordered: unordered, unknown };
}

/** The last day to order (for a job not yet locked: to lock) and still have a material in hand for the build window. */
export function orderBy(windowStart: string, leadDays: number): string {
  return addDays(windowStart, -leadDays);
}

// ---------- reorder ----------

export interface ReorderLine {
  material_id: string;
  on_hand: string;
  on_order: string;
  safety_stock: string;
  /** How far on hand + on order sits below safety stock. */
  shortfall: string;
  /** What to order: the order quantity in whole multiples, or the shortfall when none is set. */
  qty: string;
}

/**
 * A material needs reordering when stock on hand plus what is already on order falls below
 * its safety stock. Exactly at safety stock is fine. A material with no safety stock is not
 * tracked. A bin-counted material has no meaningful on-hand figure, so only its kanban card
 * can raise it. A material that already has an open request (raised and not yet on an
 * order) is not raised a second time.
 */
export function reorderLines(materials: InvMaterial[], onOrder: Record<string, Num>, openRequests: ReadonlySet<string>): ReorderLine[] {
  const out: ReorderLine[] = [];
  for (const m of materials) {
    if (!has(m.safety_stock) || m.tracking_mode === 'bin' || openRequests.has(m.id)) continue;
    const safety = rat(m.safety_stock);
    const onHand = rat(m.stock_on_hand);
    const ordered = max0(rat(onOrder[m.id]));
    const shortfall = safety.sub(onHand.add(ordered));
    if (shortfall.sign() <= 0) continue;
    out.push({
      material_id: m.id,
      on_hand: text(onHand),
      on_order: text(ordered),
      safety_stock: text(safety),
      shortfall: text(shortfall),
      qty: text(orderQty(shortfall, m.order_quantity)),
    });
  }
  return out;
}

/** The order quantity in the fewest whole multiples that cover the shortfall; the shortfall itself when no order quantity is set. */
export function orderQty(shortfall: Rat | Num, orderQuantity: Num): Rat {
  const need = shortfall instanceof Rat ? shortfall : rat(shortfall);
  const pack = rat(orderQuantity);
  if (pack.sign() <= 0) return max0(need);
  const multiples = need.sign() <= 0 ? 1n : need.div(pack).ceil();
  return pack.mul(Rat.of(multiples < 1n ? 1n : multiples));
}
